import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { styled } from '@classify/react-core-components/styles';
import Typography, { Props as TypographyProps } from '@classify/react-core-components/Typography';
import Box from '@classify/react-core-components/Box';
import Button from '@classify/react-core-components/Button';
import Stack from '@classify/react-core-components/Stack';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import { ImageCdn } from '@openclassrooms/images-cdn';
import { translate } from '@openclassrooms/translations';
import { getPathName, ROUTES } from '@openclassrooms/routing';
interface PropsCommon {
  titleLabel: string;
  detailsLabel?: string | null;
  hasButton?: boolean;
  httpStatus: number;
  customPrimaryButton?: ReactNode;
  content?: ReactNode;
  actionLink?: string;
  children?: ReactNode;
  buttonLabel?: string;
  shouldNotUpdateMeta?: boolean;
}
interface PropsWithPictureUrl extends PropsCommon {
  pictureElement?: never;
  pictureUrl?: string;
  pictureWidth?: number;
}
interface PropsWithPictureElement extends PropsCommon {
  pictureElement: ReactNode;
  pictureUrl?: never;
  pictureWidth?: never;
}
export type Props = PropsWithPictureUrl | PropsWithPictureElement;
const Wrapper = styled(Stack)(({
  theme: {
    containerQueries,
    spacing
  }
}) => ({
  flexDirection: 'column-reverse',
  gap: spacing(2),
  [containerQueries.up('md')]: {
    flexDirection: 'row',
    gap: spacing(0)
  }
}));
export const ParagraphError = t0 => {
  const $ = _c(6);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <Typography component="p" variant="bodySmall" {...props}>{children}</Typography>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export const TitleError = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mt: 2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Typography component="h2" variant="h5" sx={t1}>{children}</Typography>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const ErrorWrapperCommon = t0 => {
  const $ = _c(29);
  const {
    pictureUrl,
    titleLabel,
    detailsLabel,
    pictureWidth,
    hasButton: t1,
    httpStatus,
    customPrimaryButton: t2,
    content: t3,
    actionLink,
    children,
    buttonLabel,
    pictureElement
  } = t0;
  const hasButton = t1 === undefined ? false : t1;
  const customPrimaryButton = t2 === undefined ? null : t2;
  const content = t3 === undefined ? null : t3;
  const {
    isConnected
  } = useCurrentUserContext();
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "70vh",
      containerType: "inline-size"
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      width: {
        md: "60%"
      },
      mb: 4
    };
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const t6 = `error-${httpStatus}`;
  let t7;
  if ($[2] !== t6 || $[3] !== titleLabel) {
    t7 = <Typography component="h1" variant="h2" data-testid={t6}>{titleLabel}</Typography>;
    $[2] = t6;
    $[3] = titleLabel;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== children || $[6] !== content || $[7] !== detailsLabel) {
    t8 = children ? <div>{children}</div> : <Typography component="p" variant="subtitle1"><span dangerouslySetInnerHTML={{
        __html: detailsLabel
      }} />{content}</Typography>;
    $[5] = children;
    $[6] = content;
    $[7] = detailsLabel;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== actionLink || $[10] !== buttonLabel || $[11] !== customPrimaryButton || $[12] !== hasButton || $[13] !== isConnected) {
    t9 = (customPrimaryButton || hasButton) && <Stack flexDirection="row" gap={2} sx={{
      mt: 4,
      mb: 4
    }}>{customPrimaryButton}{hasButton && <Button variant={customPrimaryButton ? "secondary" : "primary"} component="a" href={actionLink ?? (isConnected ? getPathName(ROUTES.SPACES.STUDENT.INDEX) : getPathName(ROUTES.HOMEPAGE.INDEX))}>{buttonLabel ?? translate("errors.button_label")}</Button>}</Stack>;
    $[9] = actionLink;
    $[10] = buttonLabel;
    $[11] = customPrimaryButton;
    $[12] = hasButton;
    $[13] = isConnected;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = <Stack gap={1} sx={t5}>{t7}{t8}{t9}</Stack>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      "& > img": {
        maxWidth: "100%",
        height: "auto"
      }
    };
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== pictureElement || $[21] !== pictureUrl || $[22] !== pictureWidth) {
    t12 = pictureElement || <ImageCdn alt="" src={pictureUrl} width={pictureWidth} />;
    $[20] = pictureElement;
    $[21] = pictureUrl;
    $[22] = pictureWidth;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12) {
    t13 = <Box sx={t11}>{t12}</Box>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t10 || $[27] !== t13) {
    t14 = <ContentWrapper sx={t4}><Wrapper spacing={4} alignItems="center" justifyContent="center">{t10}{t13}</Wrapper></ContentWrapper>;
    $[26] = t10;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
};
export default ErrorWrapperCommon;